import { produce } from "immer";
import { useCallback, useEffect, useState, useTransition } from "react";

import { useDebounce } from "./useDebounce";
import { useQueryParams } from "./useQueryParams";

type FilterUpdateFunction<T> = (state: Partial<T>) => void | Partial<T>;

interface UseSuspenseQueryParamsReturn<T> {
  /** Immediate state for UI components that need instant feedback */
  optimisticFilters: Partial<T>;
  /** Immediate state for UI components that need instant feedback */
  /** Deferred state for data fetching that can be interrupted/batched/debounced
   * */
  deferredFilters: Partial<T>;
  /** Indicates when deferred synchronization is in progress */
  isPending: boolean;
  /** Function to update filters with optimistic behavior */
  setFilters: (updateFn: FilterUpdateFunction<T>) => void;
}

/**
 * A React Concurrent-safe hook for managing query parameters with optimistic updates
 * and deferred state synchronization. Useful for working with the combination of dynamic, url-driven state and Suspense boundaries.
 *
 * ## React Concurrent Features Used:
 * - **useTransition**: Marks deferred state updates as non-urgent, allowing React to
 *   prioritize user interactions over filter synchronization
 * - **startTransition**: Wraps state updates that can be interrupted by higher-priority
 *   updates (like user input)
 * - **Optimistic Updates**: Provides immediate UI feedback while background sync occurs
 *
 * ## State Management Strategy:
 * This hook maintains three layers of state:
 * 1. **optimisticFilters**: Immediate state for responsive UI (updated synchronously)
 * 2. **deferredFilters**: low priority transition-wrapped state for data fetching with an optional debounce
 * 3. **urlFilters**: URL query params as the source of truth
 *
 * ## Technical Implementation:
 * The hook creates a "temporal decoupling" between UI updates and data fetching:
 * - User interactions update optimisticFilters immediately (no blocking)
 * - Background transitions update deferredFilters after transition with an optional debounce
 * - URL sync happens asynchronously without blocking the UI thread
 *
 * This pattern prevents filter changes from blocking concurrent rendering and allows
 * React to interrupt expensive operations (like API calls) when users interact.
 *
 * @param options Configuration options
 * @param options.debounce Milliseconds to debounce filter changes (default: 0)
 * @returns Object with optimisticFilters, deferredFilters, isPending, and setFilters
 *
 * @example
 * ```typescript
 * // Basic usage with no debouncing
 * const { optimisticFilters, deferredFilters, isPending, setFilters } =
 *   useSuspenseQueryParams<FilterType>();
 *
 * // Use optimisticFilters for immediate UI updates (form inputs, checkboxes)
 * <input
 *   value={optimisticFilters.searchTerm || ''}
 *   onChange={(e) => setFilters(draft => { draft.searchTerm = e.target.value })}
 * />
 *
 * // Use deferredFilters for data fetching to keep the UI responsive while the data is loading.
 * // without this, the UI within the nearest suspense boundary will block until the data is loaded.
 * const { data } = useSuspenseQuery(query, {
 *   variables: {
 *     filter: deferredFilters
 *   }
 * });
 *
 * // isPending indicates when deferred state is synchronizing
 * {isPending && <LoadingSpinner />}
 *
 * @example
 * ```typescript
 * optionally provide a debounce time in milliseconds to prevent excessive API calls
 * const { optimisticFilters, deferredFilters, isPending, setFilters } =
 *   useSuspenseQueryParams<FilterType>({ debounce: 300 });
 * ```
 */
export function useSuspenseQueryParams<T extends Record<string, any>>({
  debounce = 0,
}: {
  debounce?: number;
} = {}): UseSuspenseQueryParamsReturn<T> {
  const { filters: urlFilters, setFilters: setUrlFilters } = useQueryParams<T>();

  // Optimistic state: Updates immediately for responsive UI interactions
  // This state is never interrupted by transitions and provides instant feedback
  const [optimisticFilters, setOptimisticFilters] = useState<Partial<T>>(urlFilters);

  // Deferred state: Updates via transitions after debounce for data fetching
  // This state can be interrupted by higher-priority updates (user interactions)
  const [deferredFilters, setDeferredFilters] = useState<Partial<T>>(urlFilters);

  // Transition state: Indicates when deferred updates are in progress
  // React uses this to prioritize urgent updates over background sync
  const [isPending, startTransition] = useTransition();

  // Debounced optimistic filters to prevent excessive API calls
  // IMPORTANT: The debounce happens before the transition to batch rapid changes
  const [debouncedFilters] = useDebounce(optimisticFilters, debounce);

  // Optimistic state is updated synchronously (before the next render)
  useEffect(() => {
    setOptimisticFilters(urlFilters);
  }, [urlFilters]);

  // Because we call setDeferredFilters inside a react transition, the resulting state
  // update is considered to be non-urgent and will be interrupted by higher-priority updates
  // Conceptually, it means:
  // - Interrupt this update if user interacts with UI
  // - Prioritize paint/layout over state synchronization
  // - Batch multiple rapid changes together as the runtime sees fit
  useEffect(() => {
    startTransition(() => {
      setDeferredFilters(debouncedFilters);
    });
  }, [debouncedFilters]);

  // Filter setter with optimistic update pattern
  // This provides immediate UI feedback while background sync occurs
  const setFilters = useCallback(
    (updateFn: FilterUpdateFunction<T>) => {
      // Calculate new state using Immer for immutable updates
      const newState = produce(optimisticFilters, updateFn);

      // STEP 1: IMMEDIATE - Update optimistic state for responsive UI
      // This happens synchronously and cannot be interrupted
      setOptimisticFilters(newState);

      // STEP 2: BACKGROUND - Sync to URL (triggers re-render but doesn't block UI)
      // The URL update will eventually sync back via the useEffects above
      setUrlFilters(() => newState);
    },
    [optimisticFilters, setUrlFilters]
  );

  return {
    // Immediate state for UI components that need instant feedback
    optimisticFilters,

    // Deferred state for data fetching that can be interrupted/batched/debounced
    deferredFilters,

    // Indicates when deferred synchronization is in progress
    isPending,

    // Function to update filters with optimistic behavior
    setFilters,
  };
}
